// @ts-nocheck

'use client'

import React, { useContext, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Scrollbar, A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import { Container } from '../../atoms/GridSystem'
import CardFeatures from '../../molecules/CardFeatures/CardFeatures.component'
import { ButtonForwardingRef } from '../../atoms/Button/Button.component'
import useSwiperRef from '../../../hooks/useSwiperRef'
import * as S from './FeatureBannerWithList.styles'
import { NormalizedFeatureBannerWithListProps } from '../../../models/organisms/FeatureBannerWithList'
import { ColorSchemeTypes } from '../../../types/ColorScheme'

const FeatureBannerWithList: React.FC<NormalizedFeatureBannerWithListProps> = ({
  sectionId,
  type,
  backgroundImage,
  items,
  colorScheme,
}) => {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const activeColorScheme = colorScheme || selectedColorScheme
  const bannerRef = useRef<HTMLElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<SwiperType | null>(null)
  const [nextEl, nextElRef] = useSwiperRef()
  const [prevEl, prevElRef] = useSwiperRef()
  const [scrollbarEl, scrollbarElRef] = useSwiperRef()
  const [hideNavigation, setHideNavigation] = useState(false)
  const scrollTriggerRef = useRef<typeof import('gsap/dist/ScrollTrigger').default | null>(null)

  const shouldShowNavigation = items && items.length > 3

  useEffect(() => {
    let gsapInstance: typeof import('gsap').default
    let ScrollTrigger: typeof import('gsap/dist/ScrollTrigger').default
    const PARALLAX_ID = 'feature-banner-parallax'

    const initParallax = async () => {
      if (typeof window === 'undefined' || !backgroundRef.current || !bannerRef.current) return

      try {
        gsapInstance = (await import('gsap')).default
        ScrollTrigger = (await import('gsap/dist/ScrollTrigger')).default
        gsapInstance.registerPlugin(ScrollTrigger)
        scrollTriggerRef.current = ScrollTrigger

        // Avoid duplicate ScrollTriggers when effect runs twice (e.g. React Strict Mode)
        const existing = ScrollTrigger.getById(PARALLAX_ID)
        if (existing) existing.kill()

        const animatedEl = backgroundRef.current
        const triggerEl = bannerRef.current
        const parallaxSpeed = 0.5

        gsapInstance.to(animatedEl, {
          yPercent: 100 * parallaxSpeed,
          ease: 'none',
          scrollTrigger: {
            id: PARALLAX_ID,
            trigger: triggerEl,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        })

        // Recalculate trigger positions after layout (fixes onUpdate not firing when created before layout is ready)
        requestAnimationFrame(() => {
          ScrollTrigger.refresh()
        })
      } catch {
        // GSAP ScrollTrigger not available
      }
    }

    initParallax()

    return () => {
      scrollTriggerRef.current?.getById(PARALLAX_ID)?.kill()
    }
  }, [])

  useEffect(() => {
    const swiper = swiperRef.current
    if (swiper) {
      const updateNavigation = () => {
        setHideNavigation(swiper.isBeginning && swiper.isEnd)
      }
      
      swiper.on('slideChange', updateNavigation)
      swiper.on('init', updateNavigation)
      updateNavigation()
    }
  }, [items])

  return (
    <S.FeatureBannerWithList
      ref={bannerRef}
      colorScheme={activeColorScheme as ColorSchemeTypes}
      as="section"
      id={sectionId}
    >
      <S.FeatureBannerWithListBackground ref={backgroundRef}>
        <Image
          src={backgroundImage.src || ''}
          alt={backgroundImage.alt || ''}
          fill
          sizes="100vw"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />
      </S.FeatureBannerWithListBackground>
      <Container>

        {shouldShowNavigation && !hideNavigation ? (
          <S.FeatureBannerWithListNavigation>
            <S.FeatureBannerWithListButtonPrev>
              <ButtonForwardingRef
                ref={prevElRef}
                variant="secondary"
                iconEnd={'chevron-left'}
                iconEndSize="big"
                inheritedColorScheme={activeColorScheme}
                round
                disabled={swiperRef.current?.isBeginning}
              />
            </S.FeatureBannerWithListButtonPrev>
            <S.FeatureBannerWithListButtonNext>
              <ButtonForwardingRef
                ref={nextElRef}
                variant="secondary"
                iconEnd={'chevron-right'}
                iconEndSize="big"
                inheritedColorScheme={activeColorScheme}
                round
                disabled={swiperRef.current?.isEnd}
              />
            </S.FeatureBannerWithListButtonNext>
          </S.FeatureBannerWithListNavigation>
        ) : null}

        <S.FeatureBannerWithListContent>
          <S.FeatureBannerWithListSwiperWrapper>
            <Swiper
              ref={swiperRef}
              onInit={(swiper) => {
                swiperRef.current = swiper
              }}
              modules={[Navigation, Scrollbar, A11y]}
              spaceBetween={24}
              slidesPerView={1.5}
              breakpoints={{
                375: {
                  slidesPerView: 1.5,
                },
                768: {
                  slidesPerView: 2,
                },
                992: {
                  slidesPerView: 3,
                },
                1280: {
                  slidesPerView: 3,
                  spaceBetween: 48,
                },
              }}
              navigation={shouldShowNavigation ? {
                prevEl,
                nextEl,
              } : false}
              scrollbar={shouldShowNavigation ? { el: scrollbarEl } : false}
              a11y={{
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
              }}
              grabCursor={true}
            >
              {items && items.length > 0 ? (
                items.map((item, i) => (
                  <SwiperSlide key={`FeatureBanner-${type}-${i}-${item.id}`}>
                    <CardFeatures
                      {...item} 
                      type={type} 
                      isLast={i === items.length - 1} 
                      colorScheme={activeColorScheme} 
                      withBackground={true}
                      buttonNoHorizontalPadding={false}
                    />
                  </SwiperSlide>
                ))
              ) : null}
            </Swiper>
            {shouldShowNavigation && !hideNavigation ? (
              <S.FeatureBannerWithListNavScrollbar ref={scrollbarElRef} colorScheme={activeColorScheme} />
            ) : null}
          </S.FeatureBannerWithListSwiperWrapper>
        </S.FeatureBannerWithListContent>
      </Container>
    </S.FeatureBannerWithList>
  )
}

export default FeatureBannerWithList

